/**
 * Kleine Zustandsmarke — „in Betrieb", „Entwurf", „gestört".
 *
 * Übernommen aus `components/data/Badge.jsx`.
 *
 * **Farbe ist nie der alleinige Träger der Aussage:** Jede Marke zeigt immer
 * auch ihren Text. Wer Rot und Grün nicht unterscheidet, liest ihn — und beim
 * Zustand eines Zählers ist das keine Feinheit.
 */
import type { ReactNode } from "react";
import { fontWeight } from "../tokens.js";

export type BadgeTone = "neutral" | "success" | "warning" | "danger" | "info" | "brand";

export interface BadgeProps {
  readonly children: ReactNode;
  readonly tone?: BadgeTone;
  /** Titel für die Maus — etwa der englische Rohwert hinter der Beschriftung. */
  readonly title?: string;
  /**
   * Ein Statuspunkt vor dem Text, wie im Entwurf (`dot`). Er trägt die Schriftfarbe
   * des Tons — eine eigene Farbe wäre ein Hexwert, der das dunkle Thema nicht erreicht.
   */
  readonly dot?: boolean;
}

const TONES: Record<BadgeTone, { background: string; color: string; border: string }> = {
  neutral: {
    background: "var(--surface-sunken)",
    color: "var(--text-body)",
    border: "var(--border-subtle)",
  },
  /* Die Töne kommen als Paar aus dem Thema (`tones` in tokens.ts, HM-107):
     Fläche, Rand und Schrift gehören zusammen, und ein Hexwert hier erreichte
     das dunkle Thema nie. */
  success: { background: "var(--tone-success-bg)", color: "var(--tone-success-fg)", border: "var(--tone-success-border)" },
  warning: { background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)", border: "var(--tone-warning-border)" },
  danger: { background: "var(--tone-danger-bg)", color: "var(--tone-danger-fg)", border: "var(--tone-danger-border)" },
  info: { background: "var(--tone-info-bg)", color: "var(--tone-info-fg)", border: "var(--tone-info-border)" },
  brand: {
    background: "var(--hm-green-50)",
    color: "var(--hm-green-700)",
    border: "var(--hm-green-200)",
  },
};

export function Badge({ children, tone = "neutral", title, dot = false }: BadgeProps) {
  const style = TONES[tone];

  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: "var(--radius-pill)",
        background: style.background,
        color: style.color,
        border: `1px solid ${style.border}`,
        fontSize: 12,
        fontWeight: fontWeight.semibold,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      {dot && (
        <span
          aria-hidden="true"
          style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", flexShrink: 0 }}
        />
      )}
      {children}
    </span>
  );
}
